import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Placeholder from '@tiptap/extension-placeholder'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import {
  Bold, Italic, UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Minus, Table as TableIcon,
  Undo2, Redo2, Save, ChevronDown, ChevronRight, ChevronLeft,
  Sparkles, Expand, Minimize2, RefreshCw, Loader2,
  BookMarked, FileDown, AlignLeft, Scissors,
} from 'lucide-react'
import api from '../../lib/api'
import { supabase } from '../../lib/supabase'
import { deduplicateChapterContent, removeDuplicateSections } from '../../utils/deduplicateContent'

marked.setOptions({ breaks: false, gfm: true })

function looksLikeMarkdown(text) {
  return /^#{1,4}\s/m.test(text) || /\*\*[^*]+\*\*/m.test(text) || /^\* /m.test(text)
}

async function markdownOrHTMLToHTML(content) {
  if (!content) return ''
  if (looksLikeMarkdown(content)) {
    const html = await marked.parse(content, { async: false })
    return DOMPurify.sanitize(html)
  }
  return content
}

// ─── Editor styles injected globally ─────────────────────────────────────────

const EDITOR_STYLES = `
.tiptap-scholar {
  outline: none;
  caret-color: #1A1410;
}
.tiptap-scholar p { margin-bottom: 1em; }
.tiptap-scholar h1 {
  font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
  margin: 2.5rem 0 1.5rem;
  color: #0f172a;
}
.tiptap-scholar h2 {
  font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  font-size: 1.125rem;
  font-weight: 700;
  text-align: left;
  margin: 2rem 0 0.75rem;
  color: #0f172a;
}
.tiptap-scholar h3 {
  font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  font-size: 1rem;
  font-weight: 700;
  font-style: italic;
  text-align: left;
  margin: 1.5rem 0 0.5rem;
  color: #0f172a;
}
.tiptap-scholar h4 {
  font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  text-align: left;
  margin: 1.25rem 0 0.5rem;
  color: #0f172a;
}
.tiptap-scholar ul, .tiptap-scholar ol { padding-left: 1.5rem; margin-bottom: 1em; }
.tiptap-scholar li { margin-bottom: 0.25em; }
.tiptap-scholar blockquote {
  border-left: 3px solid #2563eb;
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
  color: #475569;
}
.tiptap-scholar table {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
  font-size: 0.9rem;
}
.tiptap-scholar td, .tiptap-scholar th {
  border: 1px solid #c5b89a;
  padding: 0.5rem 0.75rem;
}
.tiptap-scholar th { background: #ede8df; font-weight: 600; }
.tiptap-scholar p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #b0a898;
  pointer-events: none;
  height: 0;
}
.tiptap-scholar mark { background: rgba(37,99,235,0.25); }
`

// ─── Toolbar button ───────────────────────────────────────────────────────────

function TBtn({ onClick, active, disabled, children, title, gold = false }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center w-7 h-7 rounded transition-colors disabled:opacity-40"
      style={{
        background: active ? (gold ? 'rgba(37,99,235,0.2)' : 'rgba(255,255,255,0.12)') : 'transparent',
        color: active ? (gold ? '#2563eb' : '#0C1629') : gold ? '#2563eb' : '#506280',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.07)' }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
    >
      {children}
    </button>
  )
}

function TSep() {
  return <div className="w-px h-5 mx-1 shrink-0" style={{ background: '#DDE4EF' }} />
}

// ─── Sticky Toolbar ───────────────────────────────────────────────────────────

function EditorToolbar({ editor, wordCount, saving, onSave, onInlineTransform, transforming, thesisId, chapterTitle }) {
  if (!editor) return null

  const H = ({ level }) => (
    <TBtn
      onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
      active={editor.isActive('heading', { level })}
      title={`Heading ${level}`}
    >
      <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'JetBrains Mono' }}>H{level}</span>
    </TBtn>
  )

  return (
    <div
      className="sticky top-0 z-20 flex items-center gap-0.5 px-4 overflow-x-auto shrink-0"
      style={{ background: '#FFFFFF', borderBottom: '1px solid #DDE4EF', height: 52, minWidth: 0 }}
    >
      {/* Back to chapters */}
      {thesisId && (
        <a
          href={`/app/thesis/${thesisId}/chapters`}
          className="flex items-center gap-1 px-2 py-1 rounded-md mr-2 shrink-0 font-sans text-xs transition-colors"
          style={{ color: '#506280', border: '1px solid #e2e8f0' }}
          onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <ChevronLeft style={{ width: 12, height: 12 }} />
          Chapters
        </a>
      )}
      {/* Headings */}
      <H level={1} /><H level={2} /><H level={3} /><H level={4} />
      <TSep />

      {/* Inline */}
      <TBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
        <Bold style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
        <Italic style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
        <UnderlineIcon style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
        <Strikethrough style={{ width: 13, height: 13 }} />
      </TBtn>
      <TSep />

      {/* Lists */}
      <TBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">
        <List style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list">
        <ListOrdered style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote">
        <Quote style={{ width: 13, height: 13 }} />
      </TBtn>
      <TSep />

      {/* Insert */}
      <TBtn onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table">
        <TableIcon style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule">
        <Minus style={{ width: 13, height: 13 }} />
      </TBtn>
      <TSep />

      {/* AI tools */}
      {['expand', 'simplify', 'rephrase'].map(mode => (
        <TBtn
          key={mode}
          gold
          onClick={() => onInlineTransform(mode)}
          disabled={transforming}
          title={mode.charAt(0).toUpperCase() + mode.slice(1)}
        >
          <span style={{ fontSize: 10, fontWeight: 600, fontFamily: 'Sora', textTransform: 'capitalize' }}>
            {transforming ? '…' : mode === 'expand' ? '↕' : mode === 'simplify' ? '↓' : '↺'}
          </span>
        </TBtn>
      ))}
      <TSep />

      {/* History */}
      <TBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
        <Undo2 style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
        <Redo2 style={{ width: 13, height: 13 }} />
      </TBtn>

      {/* Right side */}
      <div className="ml-auto flex items-center gap-3 shrink-0">
        <span className="font-mono text-xs text-acdyon-textFaint">{wordCount.toLocaleString()} words</span>
        <button
          onClick={onSave}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-sans text-xs font-medium transition-all"
          style={{ background: saving ? 'rgba(61,184,122,0.1)' : 'rgba(37,99,235,0.1)', color: saving ? '#3DB87A' : '#2563eb', border: `1px solid ${saving ? 'rgba(61,184,122,0.3)' : 'rgba(37,99,235,0.3)'}` }}
        >
          <Save style={{ width: 12, height: 12 }} />
          {saving ? 'Saved' : 'Save'}
        </button>
      </div>
    </div>
  )
}

// ─── Bubble Menu (floating toolbar on text selection) ────────────────────────

function FloatingAIMenu({ editor, onTransform, transforming }) {
  if (!editor) return null

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ duration: 100 }}
      shouldShow={({ editor, from, to }) => from !== to && !editor.isActive('code')}
    >
      <div
        className="flex items-center gap-0.5 px-2 py-1 rounded-full"
        style={{ background: '#FFFFFF', border: '1px solid rgba(37,99,235,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}
      >
        {['expand', 'simplify', 'rephrase'].map(mode => (
          <button
            key={mode}
            type="button"
            onClick={() => onTransform(mode)}
            disabled={transforming}
            className="flex items-center gap-1 px-2.5 py-1 rounded-full font-sans text-xs font-medium transition-colors disabled:opacity-50"
            style={{ color: '#2563eb' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(37,99,235,0.1)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <Sparkles style={{ width: 10, height: 10 }} />
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </button>
        ))}
        <div className="w-px h-4 mx-1" style={{ background: '#DDE4EF' }} />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          className="flex items-center gap-1 px-2.5 py-1 rounded-full font-sans text-xs font-medium transition-colors"
          style={{ color: '#506280' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          Highlight
        </button>
      </div>
    </BubbleMenu>
  )
}

// ─── Left panel — section navigator ──────────────────────────────────────────

function SectionNavigator({ chapter, wordCount }) {
  const [collapsed, setCollapsed] = useState(false)
  const tocSections = chapter?.toc_sections?.sections || chapter?.toc_sections || []

  return (
    <div
      className="flex flex-col shrink-0 overflow-hidden transition-all duration-200"
      style={{ width: collapsed ? 0 : 240, minWidth: collapsed ? 0 : 240, background: '#FFFFFF', borderRight: '1px solid #DDE4EF' }}
    >
      <div className="flex items-center justify-between px-4 py-3 shrink-0" style={{ borderBottom: '1px solid #DDE4EF' }}>
        <span className="font-sans text-xs font-semibold text-acdyon-textFaint uppercase tracking-wide">
          Chapter Outline
        </span>
        <button onClick={() => setCollapsed(v => !v)}>
          <ChevronRight style={{ width: 13, height: 13, color: '#8EA4BC', transform: collapsed ? 'rotate(0)' : 'rotate(180deg)' }} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {/* Chapter title */}
        <div className="px-2 py-2 mb-1">
          <p className="font-sans text-xs font-semibold text-acdyon-text leading-snug">{chapter?.title}</p>
          <p className="font-mono text-xs text-acdyon-textFaint mt-0.5">{wordCount.toLocaleString()} words</p>
        </div>

        {tocSections.map(sec => (
          <div key={sec.id}>
            <div
              className="flex items-start gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors hover:bg-acdyon-surface"
              onClick={() => {
                // Scroll editor to h2 matching section title
                const headings = document.querySelectorAll('.tiptap-scholar h2')
                for (const h of headings) {
                  if (h.textContent.includes(sec.title.split(':')[0])) {
                    h.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    break
                  }
                }
              }}
            >
              <span className="font-mono text-xs shrink-0 mt-0.5" style={{ color: '#2563eb', minWidth: 28 }}>{sec.number}</span>
              <span className="font-sans text-xs text-acdyon-textMuted leading-snug">{sec.title}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Right panel ──────────────────────────────────────────────────────────────

function RightPanel({ chapter, wordCount, status, onStatusChange, saving, lastSaved, onDeduplicate, onExport }) {
  const [notes, setNotes] = useState(chapter?.user_notes || '')
  const target   = chapter?.target_words || 0
  const pct      = target > 0 ? Math.min(Math.round((wordCount / target) * 100), 100) : 0
  const pages    = Math.round(wordCount / 250)

  const STATUSES = ['NOT_STARTED', 'DRAFT', 'REVIEWED', 'LOCKED']
  const STATUS_LABELS = {
    NOT_STARTED: 'Not Started',
    DRAFT: 'Draft',
    REVIEWED: 'Reviewed',
    LOCKED: 'Locked',
  }

  return (
    <div
      className="flex flex-col shrink-0 overflow-y-auto"
      style={{ width: 280, background: '#FFFFFF', borderLeft: '1px solid #DDE4EF' }}
    >
      {/* Status + word count */}
      <div className="p-4 space-y-4" style={{ borderBottom: '1px solid #DDE4EF' }}>
        <div>
          <label className="font-sans text-xs text-acdyon-textFaint block mb-1.5">Status</label>
          <select
            value={status}
            onChange={e => onStatusChange(e.target.value)}
            className="w-full px-3 py-2 rounded-lg font-sans text-sm text-acdyon-text outline-none"
            style={{ background: '#112236', border: '1px solid #DDE4EF', appearance: 'none', cursor: 'pointer' }}
          >
            {STATUSES.map(s => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-1">
            <span className="font-mono text-2xl font-semibold text-brand-600">{wordCount.toLocaleString()}</span>
            <span className="font-sans text-xs text-acdyon-textFaint">/ {target.toLocaleString()}</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden mb-1" style={{ background: '#1E3A5F' }}>
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${pct}%`, background: pct >= 100 ? '#3DB87A' : '#2563eb' }}
            />
          </div>
          <div className="flex justify-between">
            <span className="font-sans text-xs text-acdyon-textFaint">{pct}% of target</span>
            <span className="font-mono text-xs text-acdyon-textFaint">{pages} pp</span>
          </div>
        </div>

        {/* Save status */}
        <div className="flex items-center gap-1.5">
          {saving ? (
            <>
              <span className="w-2 h-2 rounded-full bg-brand-600 animate-pulse" />
              <span className="font-sans text-xs text-acdyon-textFaint">Saving…</span>
            </>
          ) : lastSaved ? (
            <>
              <span className="w-2 h-2 rounded-full" style={{ background: '#3DB87A' }} />
              <span className="font-sans text-xs text-acdyon-textFaint">Saved {lastSaved}</span>
            </>
          ) : null}
        </div>
      </div>

      {/* Tools */}
      <div className="p-4 space-y-3" style={{ borderBottom: '1px solid #DDE4EF' }}>
        <p className="font-sans text-xs font-semibold text-acdyon-textFaint uppercase tracking-wide">Tools</p>
        {[
          { icon: BookMarked, label: 'Add to References', onClick: null },
          { icon: FileDown,   label: 'Export Chapter',    onClick: onExport },
        ].map(({ icon: Icon, label, onClick }) => (
          <button
            key={label}
            onClick={onClick}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg font-sans text-sm text-acdyon-textMuted hover:text-acdyon-text transition-colors"
            style={{ border: '1px solid #DDE4EF' }}
          >
            <Icon style={{ width: 14, height: 14 }} />
            {label}
          </button>
        ))}
        <button
          onClick={onDeduplicate}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg font-sans text-sm transition-colors"
          style={{ color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(245,158,11,0.06)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          title="Remove duplicate paragraphs and sections from this chapter"
        >
          <Scissors style={{ width: 14, height: 14 }} />
          Fix Duplicate Content
        </button>
      </div>

      {/* Chapter notes */}
      <div className="p-4 flex-1 flex flex-col">
        <p className="font-sans text-xs font-semibold text-acdyon-textFaint uppercase tracking-wide mb-2">
          Private Notes
        </p>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Your private notes — not included in generated content…"
          rows={6}
          className="flex-1 px-3 py-2.5 rounded-lg font-sans text-xs text-acdyon-textMuted placeholder:text-acdyon-textFaint outline-none resize-none"
          style={{ background: '#112236', border: '1px solid #DDE4EF' }}
        />
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ChapterEditorPage() {
  const { id, chapterId } = useParams()
  const [searchParams]    = useSearchParams()
  const navigate          = useNavigate()

  const [thesis,              setThesis]              = useState(null)
  const [chapter,             setChapter]             = useState(null)
  const [loading,             setLoading]             = useState(true)
  const [saving,              setSaving]              = useState(false)
  const [lastSaved,           setLastSaved]           = useState('')
  const [status,              setStatus]              = useState('DRAFT')
  const [streaming,           setStreaming]           = useState(false)
  const [isGenerating,        setIsGenerating]        = useState(false)
  const [generationProgress,  setGenerationProgress]  = useState(0)
  const [transforming,        setTransforming]        = useState(false)
  const [wordCount,           setWordCount]           = useState(0)

  const saveTimerRef    = useRef(null)
  const abortRef        = useRef(null)
  const generatingRef   = useRef(false)  // sync ref for stop-generation handler

  // ── TipTap editor ────────────────────────────────────────────────────────

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight,
      Link.configure({ openOnClick: false }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Placeholder.configure({ placeholder: 'Your chapter begins here…' }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'tiptap-scholar',
        spellcheck: 'true',
      },
    },
    onUpdate: ({ editor }) => {
      const text = editor.getText()
      const wc = text.trim() ? text.trim().split(/\s+/).length : 0
      setWordCount(wc)
      scheduleSave()
    },
  })

  // ── Load data (always fresh from Supabase) ───────────────────────────────

  useEffect(() => {
    if (!editor) return
    const load = async () => {
      try {
        const [thesisRes, chapterRes] = await Promise.all([
          api.get(`/thesis/${id}`),
          api.get(`/thesis/${id}/chapters/${chapterId}`),
        ])
        setThesis(thesisRes.data)
        const ch = chapterRes.data
        setChapter(ch)
        setStatus(ch.status || 'DRAFT')

        if (ch.content) {
          // Convert markdown to HTML if needed (handles content saved before the markdown fix)
          const html = await markdownOrHTMLToHTML(ch.content)
          editor.commands.setContent(html, false)
          const wc = editor.getText().trim().split(/\s+/).filter(Boolean).length
          setWordCount(wc)
        }
      } catch {
        // silent
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, chapterId, editor]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── finalSave: write HTML content to DB directly ─────────────────────────

  const finalSave = useCallback(async (htmlContent) => {
    if (!htmlContent || !chapterId) return
    const wc = htmlContent.replace(/<[^>]+>/g, ' ').trim().split(/\s+/).filter(Boolean).length
    try {
      await api.put(`/thesis/${id}/chapters/${chapterId}`, {
        content: htmlContent, word_count: wc, status: 'DRAFT',
      })
      setStatus('DRAFT')
      setWordCount(wc)
      setLastSaved(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
    } catch { /* silent */ }
  }, [id, chapterId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Streaming mode — connect and stream into editor ───────────────────────

  useEffect(() => {
    const isStreaming = searchParams.get('streaming') === '1'
    if (!isStreaming || !editor) return

    const startStreaming = async () => {
      // Prevent duplicate generation
      if (generatingRef.current) return
      generatingRef.current = true

      setStreaming(true)
      setIsGenerating(true)
      setGenerationProgress(0)

      // Clear editor immediately
      editor.commands.clearContent()
      setWordCount(0)

      // Save cleared state to DB so refresh during generation shows empty
      try {
        await api.put(`/thesis/${id}/chapters/${chapterId}`, {
          content: '', word_count: 0, status: 'GENERATING',
        })
      } catch { /* non-fatal */ }

      try {
        const { data: { session } } = await supabase.auth.getSession()
        const token = session?.access_token

        const resp = await fetch(
          `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/thesis/${id}/chapters/${chapterId}/generate`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({}),
          }
        )

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}))
          if (resp.status === 409) {
            // Another stream already running — just listen passively
            navigate(`/app/thesis/${id}/chapters/${chapterId}`, { replace: true })
            return
          }
          throw new Error(err.error || 'Generation failed')
        }

        const reader  = resp.body.getReader()
        const decoder = new TextDecoder()
        abortRef.current = reader
        let buffer       = ''
        let accumulated  = ''
        let liveBuffer   = ''
        const targetWC   = chapter?.target_words || 4000

        while (true) {
          if (!generatingRef.current) break  // stopped by user

          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop()

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const payload = line.slice(6).trim()
            if (payload === '[DONE]') continue
            try {
              const evt = JSON.parse(payload)
              if (evt.text) {
                accumulated += evt.text
                liveBuffer  += evt.text

                // Flush to editor every ~80 chars so user sees content appearing
                if (liveBuffer.length >= 80) {
                  editor.commands.insertContent(liveBuffer)
                  liveBuffer = ''
                }

                // Update live word count
                const liveWC = accumulated.replace(/[#*`>*]/g, '').trim().split(/\s+/).filter(Boolean).length
                setWordCount(liveWC)
                setGenerationProgress(Math.min(95, (liveWC / targetWC) * 100))
              } else if (evt.error) {
                throw new Error(evt.error)
              }
            } catch (e) {
              if (e?.message && !e.message.includes('JSON')) throw e
            }
          }
        }

        // Flush any remaining live buffer
        if (liveBuffer) editor.commands.insertContent(liveBuffer)

        if (accumulated.trim()) {
          // Replace raw streamed text with properly formatted HTML
          const html = await markdownOrHTMLToHTML(accumulated)
          editor.commands.setContent(html, false)
          const finalWC = editor.getText().trim().split(/\s+/).filter(Boolean).length
          setWordCount(finalWC)
          setGenerationProgress(100)
          await finalSave(html)
        }

        navigate(`/app/thesis/${id}/chapters/${chapterId}`, { replace: true })
      } catch (err) {
        console.error('Streaming error:', err)
      } finally {
        generatingRef.current = false
        setStreaming(false)
        setIsGenerating(false)
        setGenerationProgress(0)
      }
    }

    startStreaming()
  }, [searchParams, editor]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save ─────────────────────────────────────────────────────────────────

  const scheduleSave = useCallback(() => {
    clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => save(), 3000)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const save = useCallback(async () => {
    if (!editor || !chapterId) return
    setSaving(true)
    try {
      const content  = editor.getHTML()
      const wc       = editor.getText().trim().split(/\s+/).filter(Boolean).length
      await api.put(`/thesis/${id}/chapters/${chapterId}`, {
        content,
        word_count: wc,
        status,
      })
      setLastSaved(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
    } catch { /* silent */ } finally {
      setSaving(false)
    }
  }, [editor, id, chapterId, status])

  // Cmd+S / Ctrl+S shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        save()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [save])

  // Warn on unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (saveTimerRef.current) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  // ── Status change ─────────────────────────────────────────────────────────

  const handleStatusChange = async (newStatus) => {
    setStatus(newStatus)
    try {
      await api.put(`/thesis/${id}/chapters/${chapterId}`, { status: newStatus })
    } catch { /* silent */ }
  }

  // ── Inline AI transform ───────────────────────────────────────────────────

  const handleInlineTransform = async (mode) => {
    if (!editor) return
    const { from, to } = editor.state.selection
    if (from === to) return

    const selectedText = editor.state.doc.textBetween(from, to, ' ')
    if (!selectedText.trim()) return

    setTransforming(true)
    try {
      const { data } = await api.post(`/thesis/${id}/chapters/${chapterId}/inline-transform`, {
        mode,
        selectedText,
        context: `Thesis: ${thesis?.title}. Chapter: ${chapter?.title}.`,
      })
      if (data.result) {
        editor.chain().focus().deleteRange({ from, to }).insertContentAt(from, data.result).run()
      }
    } catch { /* silent */ } finally {
      setTransforming(false)
    }
  }

  // ── Deduplicate content ───────────────────────────────────────────────────

  const handleDeduplicate = useCallback(async () => {
    if (!editor) return
    const raw  = editor.getHTML()
    const clean = removeDuplicateSections(deduplicateChapterContent(raw))
    editor.commands.setContent(clean, false)
    const wc = editor.getText().trim().split(/\s+/).filter(Boolean).length
    setWordCount(wc)
    await save()
  }, [editor, save]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Export chapter as DOCX ───────────────────────────────────────────────

  const handleExport = useCallback(async () => {
    try {
      const res = await api.post(`/thesis/${id}/chapters/${chapterId}/export/docx`, {}, { responseType: 'blob' })
      const url  = URL.createObjectURL(res.data)
      const a    = document.createElement('a')
      a.href     = url
      a.download = `chapter-${chapter?.chapter_number || chapterId}.docx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export failed:', err)
    }
  }, [id, chapterId, chapter]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Stop streaming ────────────────────────────────────────────────────────

  const stopStreaming = async () => {
    generatingRef.current = false
    try { abortRef.current?.cancel?.() } catch { /* ignore */ }
    setStreaming(false)
    setIsGenerating(false)
    setGenerationProgress(0)

    // Save whatever has been generated so far
    const html = editor.getHTML()
    if (html && html !== '<p></p>') {
      await finalSave(html)
    }
    navigate(`/app/thesis/${id}/chapters/${chapterId}`, { replace: true })
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100vh' }}>
        <div className="w-6 h-6 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
      </div>
    )
  }

  return (
    <>
      {/* Inject editor styles */}
      <style>{EDITOR_STYLES}</style>

      <div className="flex" style={{ height: 'calc(100vh - 4rem)', overflow: 'hidden' }}>

        {/* Left — section navigator */}
        <SectionNavigator chapter={chapter} wordCount={wordCount} />

        {/* Centre — editor */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden" style={{ background: '#060B14' }}>
          {/* Toolbar */}
          <EditorToolbar
            editor={editor}
            wordCount={wordCount}
            saving={saving}
            onSave={save}
            onInlineTransform={handleInlineTransform}
            transforming={transforming}
            thesisId={id}
            chapterTitle={chapter?.title}
          />

          {/* Floating AI bubble menu */}
          <FloatingAIMenu
            editor={editor}
            onTransform={handleInlineTransform}
            transforming={transforming}
          />

          {/* Scrollable parchment area */}
          <div
            className="flex-1 overflow-y-auto"
            style={{ background: '#1A1410' }}
          >
            <div
              className="mx-auto my-8"
              style={{
                maxWidth: 860,
                background: '#F5F0E8',
                padding: '60px 80px',
                minHeight: 'calc(100vh - 4rem)',
                boxShadow: '0 0 60px rgba(0,0,0,0.6)',
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontSize: 16,
                lineHeight: 1.8,
                color: '#1A1410',
              }}
            >
              <EditorContent editor={editor} />
            </div>
          </div>

          {/* Generation progress overlay */}
          {isGenerating && (
            <div
              className="shrink-0 flex flex-col"
              style={{ background: 'rgba(15,23,42,0.92)', borderTop: '1px solid rgba(37,99,235,0.3)' }}
            >
              {/* Progress bar */}
              <div className="h-0.5 w-full" style={{ background: 'rgba(37,99,235,0.15)' }}>
                <div
                  className="h-full transition-all duration-500"
                  style={{ width: `${generationProgress}%`, background: '#2563eb' }}
                />
              </div>
              <div className="flex items-center justify-between px-6 py-3">
                <div className="flex items-center gap-3">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: '#2563eb', animation: 'pulse 1.5s ease-in-out infinite' }}
                  />
                  <span className="font-sans text-sm" style={{ color: '#e2e8f0' }}>
                    Writing Chapter {chapter?.chapter_number}…
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono text-xs" style={{ color: '#94a3b8' }}>
                    {wordCount.toLocaleString()} words
                  </span>
                  <button
                    onClick={stopStreaming}
                    className="px-3 py-1 rounded-lg font-sans text-xs font-medium transition-colors"
                    style={{ color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,113,113,0.1)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    Stop
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right — metadata + tools */}
        <RightPanel
          chapter={chapter}
          wordCount={wordCount}
          status={status}
          onStatusChange={handleStatusChange}
          saving={saving}
          lastSaved={lastSaved}
          onDeduplicate={handleDeduplicate}
          onExport={handleExport}
        />
      </div>

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); opacity: 0.6; }
          50% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </>
  )
}
